import recorderWorkletUrl from './recorder.worklet.ts?worker&url';
import { generateSweep, sweepSpec, type Sweep, type SweepPresetName } from '../dsp/sweep.ts';

export interface CaptureOptions {
  preset?: SweepPresetName;
  /** Extra recording after the sweep ends, to catch the tail. */
  tailSec?: number;
  /** Silence before the sweep starts, so the worklet is definitely running. */
  leadInSec?: number;
  /** Output level, 0..1. */
  playbackGain?: number;
  onStatus?: (status: string) => void;
  signal?: AbortSignal;
}

export interface CaptureResult {
  recording: Float32Array;
  sampleRate: number;
  sweep: Sweep;
  warnings: string[];
  inputLabel: string;
}

/**
 * The constraints that matter, and why each one is fatal if left on.
 *
 * Echo cancellation is designed to detect sound that the device just played
 * and subtract it from the microphone signal. That is a precise description of
 * the measurement - every echo Chirpr looks for is exactly what AEC exists to
 * destroy. Automatic gain control rides the level during the sweep, which
 * turns a linear system into a time-varying one and smears the deconvolution.
 * Noise suppression is a spectral gate that eats reverb tails, which is the
 * signal RT60 is computed from.
 *
 * Browsers turn all three on by default.
 */
export const MEASUREMENT_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  autoGainControl: false,
  noiseSuppression: false,
  channelCount: 1,
};

export function isCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices?.getUserMedia !== undefined &&
    typeof AudioWorkletNode !== 'undefined'
  );
}

/** Explains why capture will fail before the user presses anything, or null. */
export function captureBlocker(): string | null {
  if (typeof window === 'undefined') return 'No browser environment.';
  if (!window.isSecureContext) {
    return 'Microphone access needs HTTPS. Serve the page over TLS, or use localhost.';
  }
  if (!isCaptureSupported()) return 'This browser has no AudioWorklet or getUserMedia.';
  return null;
}

/**
 * Play a sweep, record the room, hand back the raw capture.
 *
 * Must be called from a user gesture: both AudioContext.resume and
 * getUserMedia require one, and iOS is strict about it.
 *
 * Note what this function does *not* do - it never tries to know when the
 * sweep actually left the speaker. Output latency plus input latency is
 * unknown, device-specific, and drifts; `AudioContext.outputLatency` is
 * missing or wrong on most phones. It does not matter. The direct sound
 * travels 5 cm and is by far the loudest thing in the recording, so the
 * analysis uses it as its own zero reference and the entire latency question
 * disappears.
 */
export async function captureRoom(options: CaptureOptions = {}): Promise<CaptureResult> {
  const {
    preset = 'room',
    tailSec = 1.5,
    leadInSec = 0.3,
    playbackGain = 0.9,
    onStatus = () => {},
    signal,
  } = options;

  const blocker = captureBlocker();
  if (blocker) throw new Error(blocker);

  const warnings: string[] = [];
  onStatus('Requesting microphone...');

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: MEASUREMENT_CONSTRAINTS,
    video: false,
  });

  let context: AudioContext | undefined;
  try {
    const track = stream.getAudioTracks()[0];
    const settings = track?.getSettings() ?? {};
    // Constraints are requests, not guarantees. Several Android builds force
    // echo cancellation on regardless, and the user deserves to know that the
    // number they are about to read is compromised.
    if (settings.echoCancellation) warnings.push('Device kept echo cancellation on - echoes will be suppressed.');
    if (settings.autoGainControl) warnings.push('Device kept automatic gain control on - levels will drift during the sweep.');
    if (settings.noiseSuppression) warnings.push('Device kept noise suppression on - the reverb tail will be gated.');

    context = createContext();
    if (context.state === 'suspended') await context.resume();
    const sampleRate = context.sampleRate;
    if (sampleRate < 44100) warnings.push(`Input is running at ${sampleRate} Hz, which limits the usable band.`);

    onStatus('Loading audio worklet...');
    await loadRecorderModule(context);

    const sweep = generateSweep(sweepSpec(sampleRate, preset));

    const source = context.createMediaStreamSource(stream);
    const recorder = new AudioWorkletNode(context, 'chirpr-recorder', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
    });

    // A worklet with no downstream connection is not guaranteed to be pulled.
    // Routing it to the destination through a muted gain keeps it running
    // without adding the microphone to the speaker.
    const sink = context.createGain();
    sink.gain.value = 0;
    source.connect(recorder).connect(sink).connect(context.destination);

    const chunks: Float32Array[] = [];
    let total = 0;
    recorder.port.onmessage = (event: MessageEvent): void => {
      const data = event.data as { type?: string; samples?: Float32Array };
      if (data?.type === 'chunk' && data.samples) {
        chunks.push(data.samples);
        total += data.samples.length;
      }
    };

    const buffer = context.createBuffer(1, sweep.samples.length, sampleRate);
    buffer.getChannelData(0).set(sweep.samples);
    const player = context.createBufferSource();
    player.buffer = buffer;
    const outputGain = context.createGain();
    outputGain.gain.value = playbackGain;
    player.connect(outputGain).connect(context.destination);

    const startAt = context.currentTime + leadInSec;
    player.start(startAt);
    onStatus('Measuring - hold still and stay quiet...');

    const durationSec = leadInSec + sweep.spec.durationSec + tailSec + 0.2;
    await waitFor(durationSec * 1000, signal);

    recorder.port.postMessage('stop');
    await waitFor(60);
    player.stop();
    source.disconnect();
    recorder.disconnect();
    sink.disconnect();

    const recording = concat(chunks, total);
    if (recording.length === 0) throw new Error('No audio was captured.');

    onStatus('Analysing...');
    return { recording, sampleRate, sweep, warnings, inputLabel: track?.label ?? 'microphone' };
  } finally {
    for (const track of stream.getTracks()) track.stop();
    if (context && context.state !== 'closed') void context.close();
  }
}

function createContext(): AudioContext {
  try {
    // 48 kHz is the sane default: one sample is 3.6 mm of range, and phone
    // hardware runs there natively so nothing gets resampled behind our back.
    return new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' });
  } catch {
    return new AudioContext();
  }
}

/**
 * Load the recorder worklet, falling back to a blob URL.
 *
 * `addModule` can fail on a cross-origin bundle or a host that serves the
 * chunk with an unhelpful content type. Re-fetching the same compiled module
 * and handing it over as a blob sidesteps both without duplicating the
 * processor source.
 */
async function loadRecorderModule(context: AudioContext): Promise<void> {
  try {
    await context.audioWorklet.addModule(recorderWorkletUrl);
  } catch (error) {
    const response = await fetch(recorderWorkletUrl);
    if (!response.ok) throw error;
    const source = await response.text();
    const blobUrl = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
    try {
      await context.audioWorklet.addModule(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }
}

function waitFor(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Measurement cancelled', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException('Measurement cancelled', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function concat(chunks: Float32Array[], total: number): Float32Array {
  const out = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}
